'use client';

import { Fragment, useEffect, useMemo, useState } from 'react';
import { ReactComponent as Bars } from '@assets/icons/bars.svg';
import { ReactComponent as Check } from '@assets/icons/check.svg';
import { ReactComponent as ChevronRight } from '@assets/icons/chevron-right.svg';
import DefaultUserAvatar from '@assets/icons/default-user-avatar.svg';
import { ReactComponent as Times } from '@assets/icons/times.svg';
import config from 'config';
import { ROUTES } from 'constant';
import { usePathname, useRouter } from 'next/navigation';

import {
  Box,
  Button,
  Collapse,
  Flex,
  Text,
  useDisclosure,
  useTheme
} from '@chakra-ui/react';

import { useDashboardSectionAccess } from 'hooks';
import { useAppSelector } from 'store/hooks';
import { userSelector } from 'store/reducers/userSlice';

import { getPreviousPathname, isNonReturnablePath } from 'utils';
import {
  filterDashboardNavItems,
  findActiveDashboardNavTab,
  isDashboardNavLinkActive
} from 'utils/helpers/dashboardNav';

import { TabType } from 'types';

import { Avatar } from 'components/Avatar';
import { AccountBalance } from 'components/ProfileSidebar/components';
import { Divider } from 'ui/Divider';

const {
  dashboard: {
    tabs,
    mobileTabs,
    sidebar: { isReferFriend }
  }
} = config;

const menuButtonStaticStyles = {
  position: 'relative',
  borderRadius: 0,
  opacity: 1,
  bg: 'transparent',
  p: 0,
  minW: 'none',
  ml: '55px',

  '& > span': {
    margin: 0
  },

  '&:hover, &:active, &[data-active]': {
    opacity: 1
  },

  '&::before': {
    content: '""',
    position: 'absolute',
    display: 'block',
    width: '80px',
    height: '80px',
    top: '50%',
    left: '50%',
    transform: 'translate(-70%, -50%)'
  }
};

// Absolutely centered on the bar (and therefore the screen) — as a flex child
// it would centre in the leftover space between the unequal left/right
// controls, sitting visibly off-centre.
const labelStyles = {
  position: 'absolute',
  left: '50%',
  top: '50%',
  transform: 'translate(-50%, -50%)',
  maxW: 'calc(100% - 160px)',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  pointerEvents: 'none',
  textTransform: 'uppercase',
  fontSize: '16px',
  lineHeight: '120%',
  textAlign: 'center'
};

const menuListStaticStyles = {
  padding: '0px 15px',
  border: 0,
  borderRadius: 0,
  transition: 'background 0s, opacity 0s'
};

const menuItemStaticStyles = {
  textTransform: 'uppercase',
  padding: '20px 15px',
  w: '100%',
  justifyContent: 'space-between',
  fontSize: '13px',
  lineHeight: '18px',
  cursor: 'pointer',

  '> span': {
    margin: 0
  }
};

const menuDividerStaticStyles = {
  opacity: 1,
  margin: 0,

  '&:last-of-type': {
    display: 'none'
  }
};

const Menu = () => {
  const router = useRouter();
  const {
    colors: {
      palette: {
        components: {
          ui: { menu: palette }
        }
      }
    }
  } = useTheme();
  const pathname = usePathname();
  const isProfileDetails = pathname === ROUTES.profile;
  const { isOpen, onToggle } = useDisclosure();

  // Figma note: the back arrow returns to the prior page; coming from a
  // completed-transaction (or auth) page returns to the dashboard instead.
  // When no prior page is known (deep link, fresh tab) the dashboard — the
  // profile's natural parent — is used, so the arrow is always available.
  const [backMode, setBackMode] = useState<'back' | 'dashboard'>('dashboard');

  useEffect(() => {
    if (!isProfileDetails) {
      return;
    }

    const previousPath = getPreviousPathname(pathname);

    setBackMode(
      previousPath && !isNonReturnablePath(previousPath) ? 'back' : 'dashboard'
    );
  }, [isProfileDetails, pathname]);

  const handleBack = () => {
    if (backMode === 'dashboard') {
      router.push(ROUTES.dashboard);
    } else {
      router.back();
    }
  };
  const { user } = useAppSelector(userSelector);
  const { isSectionEnabled } = useDashboardSectionAccess();

  const tabsData = useMemo(() => {
    const arr = filterDashboardNavItems<TabType>(
      [...(tabs ?? []), ...(mobileTabs ?? [])],
      isSectionEnabled
    );

    if (isReferFriend) {
      arr.push({
        label: 'REFER A FRIEND',
        link: ROUTES.referFriend
      });
    }

    const seenLinks = new Set<string>();

    return arr.filter((tab) => {
      if (seenLinks.has(tab.link)) {
        return false;
      }

      seenLinks.add(tab.link);
      return true;
    });
  }, [isSectionEnabled]);

  const getLabel = () => {
    const item = findActiveDashboardNavTab(pathname, tabsData);
    return item?.label || tabsData[0].label;
  };

  const menuListStyles = {
    ...menuListStaticStyles,
    background: palette.menuListBackground
  };

  const menuItemStyles = {
    ...menuItemStaticStyles,
    background: palette.menuItemBackground,

    '&.active-link': {
      fontWeight: palette.menuItemActiveFontWeight
    }
  };

  const menuDividerStyles = {
    ...menuDividerStaticStyles,
    borderColor: palette.menuDividerBorderColor
  };

  const onMenuClick = (link: string) => {
    onToggle();
    router.push(link);
  };

  return (
    <Flex
      position='relative'
      alignItems='center'
      justifyContent='space-between'
      px='20px'
      py='12px'
      background={palette.menuBackground}
    >
      {isProfileDetails ? (
        // The profile page shows a large hero avatar right below this bar, so
        // the thumbnail is replaced by a back arrow (or a spacer when there is
        // no prior page) — same footprint keeps the title centered.
        <Flex
          as='button'
          type='button'
          aria-label='Go back'
          onClick={handleBack}
          w={{ base: '40px', md: '60px' }}
          h={{ base: '40px', md: '60px' }}
          flexShrink={0}
          align='center'
          justify='flex-start'
          cursor='pointer'
        >
          <ChevronRight
            width={10}
            height={20}
            fill={palette.menuIconColor}
            style={{ transform: 'rotate(180deg)' }}
          />
        </Flex>
      ) : (
        <Avatar
          key={`${user?.givname} ${user?.surname}`}
          src={user?.photo || DefaultUserAvatar}
          w={{ base: '40px', md: '60px' }}
          h={{ base: '40px', md: '60px' }}
          flexShrink={0}
          alt={`${user?.givname} ${user?.surname}`}
          borderRadius='50%'
          borderWidth={3}
          borderColor={palette.menuAvatarBorderColor}
          useBadges={false}
          isMember={!!user?.is_member}
          isVIP={user?.is_vip === 1}
          isInfluencer={user?.is_influencer === 1}
        />
      )}
      <Text color={palette.menuLabelColor} sx={labelStyles}>
        {getLabel()}
      </Text>
      <Button
        onClick={onToggle}
        rightIcon={
          isOpen ? (
            <Times width='20px' height='20px' fill={palette.menuIconColor} />
          ) : (
            <Bars width='20px' height='20px' fill={palette.menuIconColor} />
          )
        }
        sx={menuButtonStaticStyles}
      />
      <Collapse
        in={isOpen}
        style={{
          position: 'absolute',
          width: '100%',
          zIndex: 10,
          top: '100%',
          left: 0
        }}
      >
        <Box>
          {!pathname.startsWith(ROUTES.absoluteProfilePath) && (
            <AccountBalance isMobile balance={user?.balance ?? 0} />
          )}
          <Box sx={menuListStyles}>
            {tabsData.map(({ link, label }: TabType) => {
              const isActive = isDashboardNavLinkActive(pathname, link);

              return (
                <Fragment key={link}>
                  <Flex
                    onClick={() => onMenuClick(link)}
                    sx={menuItemStyles}
                    className={isActive ? 'active-link' : ''}
                  >
                    {label}
                    <Box>
                      {isActive ? (
                        <Check
                          fill={palette.menuIconColor}
                          width='20px'
                          height='20px'
                        />
                      ) : undefined}
                    </Box>
                  </Flex>
                  <Divider sx={menuDividerStyles} />
                </Fragment>
              );
            })}
          </Box>
        </Box>
      </Collapse>
    </Flex>
  );
};

export default Menu;
